/**
 * TODO(orquestrador): esta página deve só ler a query, carregar o paciente,
 * guardar o wizard e renderizar o passo ativo.
 *
 * Extrair para src/components/paciente/agendamento/:
 * - CadastroPacienteOnline
 * - AlertaPreparoProcedimento
 * - FormAtualizarCelular
 * - PassoConvenio, PassoMedico, PassoDataHorario, PassoResumo,
 *   PassoConfirmacao, PassoSucesso
 *
 * Extrair para helpers/util (funções puras, sem setState):
 * - filtrarMedicosPorConvenio
 * - montarPayloadAgendamentoOnline (codProcedi, protocolo, situação)
 * - mensagemWhatsAppAgendamento
 * - diaCivilAgenda / formatarDiaCivil / slotPertenceAoDia
 *
 * Cada passo recebe um objeto pequeno — não 20+ args posicionais.
 * Só montar o conteúdo do activeStep (hoje o Stepper pinta os 6 passos).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { IoMdClipboard } from 'react-icons/io';
import { FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import { format, addMonths, parseISO, differenceInYears } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { ptBR } from 'date-fns/locale';
import { Button, Alert, Card, Form, Row, Col } from 'react-bootstrap';
import InputMask from 'react-bootstrap-maskedinput';
import styles from '../../styles.module.css';
import api from '../../services/api';
import { normalizeWhatsappPhone } from '../../services/pdfWhatsapp';
import { convenioAgendaPac } from '../../helpers/agenda/convenios';
import { medicoAgendaPac } from '../../helpers/agenda/medicos';
import { formatarHora, dataExtenso, formatarData } from '../../util/utils';
import AppFooter from '../../components/AppFooter';
import IcmHeaderImage from '../../components/IcmHeaderImage';
import validarCPF from '../../util/validarCpf';
import useAgendaAberta from '../../hooks/useAgendaAberta';

// TODO(orquestrador): mover para src/util/agendaDatas.js e cobrir com teste
// (ISO `YYYY-MM-DD`, Date local ao meio-dia, string pt-BR).
const diaCivilAgenda = valor => {
  if (!valor) return '';
  if (valor instanceof Date) return format(valor, 'yyyy-MM-dd');
  const texto = String(valor);
  if (/^\d{4}-\d{2}-\d{2}/.test(texto)) return texto.slice(0, 10);
  return formatarData(valor)
    .split('/')
    .reverse()
    .join('-');
};

const slotPertenceAoDia = (objAgenda, dateAgenda) =>
  Boolean(
    objAgenda?.DATAAGENDA &&
    diaCivilAgenda(objAgenda.DATAAGENDA) === diaCivilAgenda(dateAgenda)
  );

function mensagemConfirmacaoWhatsApp(enviado, code) {
  if (enviado) return 'Uma confirmação foi enviada para o seu WhatsApp!';
  if (code === 'NUMBER_NOT_ON_WHATSAPP') {
    return 'Este celular não está no WhatsApp. Guarde a confirmação desta tela.';
  }
  if (code === 'WHATSAPP_UNAVAILABLE') {
    return 'Não foi possível enviar a confirmação agora. Os dados estão nesta tela. Se o celular tiver WhatsApp, use o botão abaixo.';
  }
  return 'Não foi possível enviar a confirmação automaticamente. Use o botão abaixo para enviar pelo WhatsApp.';
}

const formatarDiaCivil = valor => {
  const iso = diaCivilAgenda(valor);
  if (!iso) return '';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
};

// TODO(orquestrador): apagar este switch. Cada case vira um componente de passo.
function getStepContent(
  step,
  selectedIdxMed,
  selectedIdxConv,
  selectedIdxAgenda,
  handleMedicoClick,
  handleConvenioClick,
  medicos,
  paciente,
  convenios,
  dateFormatted,
  dateFormattedExtenso,
  horariosAgenda,
  carregandoHorarios,
  handleHorarioClick,
  formatarHora,
  handleChangeDtAgenda,
  dateAgenda,
  nextDates,
  nomeMedico,
  siglaConvenio,
  horario,
  handleConfirm,
  handleReset,
  protocolo,
  handleEnviarWhatsApp,
  handleNext,
  isSubmitting,
  whatsappEnviado,
  whatsappFalha
) {
  switch (step) {
    case 0:
      return (
        <List dense component="nav" aria-label="Lista de Convênios">
          {convenios.map((convenio, idxConv) => (
            <ListItem
              button
              key={convenio.codConv}
              selected={selectedIdxConv === idxConv}
              onClick={() =>
                handleConvenioClick(idxConv, convenio.codConv, convenio.sigla)
              }
            >
              <ListItemText>{convenio.sigla}</ListItemText>
            </ListItem>
          ))}
        </List>
      );
    case 1:
      return (
        <List dense component="nav" aria-label="Lista de Médicos">
          {medicos.map((medico, idxMed) => (
            <ListItem
              button
              key={medico.codigo}
              selected={selectedIdxMed === idxMed}
              onClick={() =>
                handleMedicoClick(idxMed, medico.codigo, medico.medico)
              }
            >
              <ListItemText>{medico.medico}</ListItemText>
            </ListItem>
          ))}
        </List>
      );
    case 2:
      return (
        <>
          <DatePicker
            selected={dateAgenda}
            onChange={handleChangeDtAgenda}
            inline
            locale={ptBR}
            minDate={new Date()}
            maxDate={addMonths(new Date(), 12)}
            dayClassName={date => {
              const normalize = d => {
                let dt = d instanceof Date ? new Date(d) : new Date(String(d));
                dt.setHours(0, 0, 0, 0);
                return dt.getTime();
              };
              const current = normalize(date);
              const isAvailable = nextDates.some(d => normalize(d) === current);
              return isAvailable ? styles.nextAppts : undefined;
            }}
          />

          {carregandoHorarios ? (
            <Alert variant="info">Buscando horários...</Alert>
          ) : horariosAgenda.length > 0 ? (
            <>
              <Alert variant="success" className="py-2 mb-2">
                <small>
                  <strong>
                    {horariosAgenda.length === 1
                      ? 'Há apenas 1 horário disponível. Clique nele para selecioná-lo.'
                      : 'Clique no horário desejado para selecioná-lo.'}
                  </strong>
                </small>
              </Alert>
              {horariosAgenda.map((a, idxAgenda) => (
                <ListItem
                  button
                  key={a.CODAGENDA}
                  selected={selectedIdxAgenda === idxAgenda}
                  onClick={() => handleHorarioClick(idxAgenda, a.HORARIO, a)}
                >
                  <ListItemIcon>
                    <IoMdClipboard />
                  </ListItemIcon>
                  <ListItemText>
                    {formatarHora(a.HORARIO)} - {a.ATIVIDADE}
                  </ListItemText>
                </ListItem>
              ))}
            </>
          ) : (
            <Alert variant="danger">NÃO TEM HORÁRIO NO DIA SELECIONADO!</Alert>
          )}
        </>
      );
    case 3:
      return (
        <Card className="border-primary">
          <Card.Header className="bg-primary text-white font-weight-bold">
            Resumo do Agendamento
          </Card.Header>
          <Card.Body>
            <p>
              <strong>{paciente?.NOMEPAC}</strong>, estamos quase concluindo o
              seu agendamento. Confira os dados abaixo:
            </p>
            <ul className="mb-3">
              <li>
                <strong>Médico:</strong> {nomeMedico}
              </li>
              <li>
                <strong>Convênio:</strong> {siglaConvenio}
              </li>
              <li>
                <strong>Data:</strong> {dateFormatted} ({dateFormattedExtenso})
              </li>
              <li>
                <strong>Horário:</strong> {formatarHora(horario)}
              </li>
            </ul>
            <p className="mb-0 text-muted">
              Clique em <strong>AGENDAR</strong> abaixo para prosseguir para a
              tela final de confirmação.
            </p>
          </Card.Body>
          <Card.Footer className="text-center">
            <Button
              variant="success"
              size="lg"
              onClick={handleNext}
              disabled={!horario}
              aria-label="Prosseguir para confirmação do agendamento"
            >
              AGENDAR
            </Button>{' '}
            <Button variant="secondary" size="lg" onClick={handleReset}>
              Alterar
            </Button>
          </Card.Footer>
        </Card>
      );
    case 4:
      return (
        <Card>
          <Card.Header className="font-weight-bold">
            Confirmação Final do Agendamento
          </Card.Header>
          <Card.Body>
            <ul>
              <li>
                {paciente?.NOMEPAC}, por favor confira as informações finais e
                clique em <strong>CONFIRMAR AGENDAMENTO</strong>.
              </li>
              <br />
              <strong>
                Agendamento com {nomeMedico?.toUpperCase()}
                <br />
                Convênio: {siglaConvenio} <br />
                Data: {dateFormattedExtenso} às {formatarHora(horario)}.<br />
                Chegar com 15 minutos de antecedência.
              </strong>
              <br />
              <br />
              <li>
                <strong>LOCAL:</strong> Av. Vicente Ferreira 780 – ao lado do
                P.S. da Santa Casa
              </li>
              <li>
                <strong>ESTACIONAMENTO:</strong> Rua Amazonas, 777.
              </li>
            </ul>
          </Card.Body>
          <Card.Footer className="text-center">
            <Button
              variant="success"
              size="lg"
              onClick={handleConfirm}
              disabled={!horario || !protocolo || isSubmitting}
              aria-label="Confirmar agendamento"
            >
              {isSubmitting ? 'Confirmando...' : 'CONFIRMAR AGENDAMENTO'}
            </Button>{' '}
            <Button variant="warning" size="lg" onClick={handleReset}>
              Alterar
            </Button>
          </Card.Footer>
        </Card>
      );
    case 5:
      return (
        <Card className="border-success">
          <Card.Header className="bg-success text-white font-weight-bold text-center">
            <FaCheckCircle size={24} className="mr-2" />
            Agendamento Confirmado!
          </Card.Header>
          <Card.Body>
            <Alert variant="success" className="text-center">
              <h5>🎉 Parabéns, {paciente?.NOMEPAC}!</h5>
              <p>Seu agendamento foi realizado com sucesso.</p>
              <p className="mb-0">
                <FaWhatsapp size={20} className="mr-1" />
                <strong>
                  {mensagemConfirmacaoWhatsApp(whatsappEnviado, whatsappFalha)}
                </strong>
              </p>
            </Alert>
            <Card className="mb-3">
              <Card.Header className="font-weight-bold">
                📋 Dados do Agendamento
              </Card.Header>
              <Card.Body>
                <p>
                  <strong>Protocolo:</strong>{' '}
                  <span className="text-primary">{protocolo}</span>
                </p>
                <p>
                  <strong>Paciente:</strong> {paciente?.NOMEPAC}
                </p>
                <p>
                  <strong>Médico:</strong> {nomeMedico}
                </p>
                <p>
                  <strong>Convênio:</strong> {siglaConvenio}
                </p>
                <p>
                  <strong>Data:</strong> {dateFormattedExtenso} - (
                  {dateFormatted})
                </p>
                <p>
                  <strong>Horário:</strong> {formatarHora(horario)}
                </p>
                <hr />
                <p>
                  <strong>📍 Local:</strong> Av. Vicente Ferreira 780 – ao lado
                  do P.S. da Santa Casa
                </p>
                <p>
                  <strong>🚗 Estacionamento:</strong> Rua Amazonas, 777
                </p>
                <p className="text-warning">
                  <strong>⚠️ Chegar com 15 minutos de antecedência.</strong>
                </p>
              </Card.Body>
            </Card>
          </Card.Body>
          {whatsappFalha !== 'NUMBER_NOT_ON_WHATSAPP' && (
            <Card.Footer className="text-center">
              <p className="text-muted mb-2">
                Não recebeu a mensagem? Clique abaixo para reenviar:
              </p>
              <Button
                variant="outline-success"
                onClick={handleEnviarWhatsApp}
                disabled={!horario || !protocolo}
                aria-label="Reenviar confirmação via WhatsApp"
                className="d-flex align-items-center justify-content-center mx-auto"
              >
                <FaWhatsapp size={20} className="mr-2" />
                Reenviar via WhatsApp
              </Button>
            </Card.Footer>
          )}
        </Card>
      );
    default:
      return 'N/A';
  }
}

const steps = [
  'Selecione um Convênio',
  'Selecione um Médico',
  'Selecione uma Data e Horário',
  'Conferir o Agendamento',
  'Concluir o Agendamento',
  'Confirmação do Agendamento',
];

const STATUS_PACIENTE_PENDENTE = 3;

const isPacientePendente = pac =>
  Number(pac?.SETORPAC) === STATUS_PACIENTE_PENDENTE;

export default function Agendamento() {
  const location = useLocation();
  const [paciente, setPaciente] = useState();
  const [cpfInvalido, setCpfInvalido] = useState(false);
  const [pacientePendente, setPacientePendente] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [codMedico, setCodMedico] = useState();
  const [nomeMedico, setNomeMedico] = useState();
  const [codConvenio, setCodConvenio] = useState();
  const [siglaConvenio, setSiglaConvenio] = useState();
  const [dateAgenda, setDateAgenda] = useState(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    return now;
  });
  const [horario, setHorario] = useState();
  // TODO(orquestrador): `null` em vez de `[]` — agenda é um slot, não uma lista.
  const [agenda, setAgenda] = useState([]);
  const [selectedIdxMed, setSelectedIdxMed] = useState();
  const [selectedIdxConv, setSelectedIdxConv] = useState();
  const [selectedIdxAgenda, setSelectedIdxAgenda] = useState();
  const [medicos, setMedicos] = useState(medicoAgendaPac);
  const [activeStep, setActiveStep] = useState(0);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [isNewPhone, setIsNewPhone] = useState(false);
  const [isMensagem, setIsMensagem] = useState(false);
  const [isAgendamento, setIsAgendamento] = useState(false);
  const [datesAvailable, setDatesAvailable] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whatsappEnviado, setWhatsappEnviado] = useState(false);
  const [whatsappFalha, setWhatsappFalha] = useState(null);
  const [alert, setAlert] = useState({
    show: false,
    variant: 'primary',
    mensagem: '',
  });

  const dateFormatted = format(dateAgenda, 'dd/MM/yyyy', { locale: pt });
  const dateFormattedExtenso = dataExtenso(dateFormatted);
  const dateAgendaFormatted = dateAgenda.toISOString();

  // Exemplo 1 – Consulta com qualquer médico
  // 👉 https://www.icm-marilia.com/agendamento?cpf=12345678900&medico=TODOS&procedimento=CONSULTA
  // Exemplo 2 – ECO-FETAL com Dr. Luiz Gustavo (adulto)
  // 👉 https://www.icm-marilia.com/agendamento?cpf=12345678900&medico=4860&procedimento=ECO-FETAL

  const searchParams = new URLSearchParams(location.search);
  const cpf = searchParams.get('cpf');
  const procedimento = searchParams.get('procedimento');
  const medicoParam = searchParams.get('medico');
  const skipStepsMedicos = [1981, 4860, 2921];
  const cardioPediatras = [4190, 4613, 4860];
  const codMedicoParam = Number(medicoParam);
  const devePularPassoMedico =
    Number.isFinite(codMedicoParam) &&
    skipStepsMedicos.includes(codMedicoParam);

  const { horariosAgenda, carregandoHorarios } = useAgendaAberta(
    codMedico,
    dateAgendaFormatted,
    procedimento
  );

  const dateFormattedSlot = agenda?.DATAAGENDA
    ? formatarDiaCivil(agenda.DATAAGENDA)
    : dateFormatted;
  const dateFormattedExtensoSlot = dateFormattedSlot
    ? dataExtenso(dateFormattedSlot)
    : dateFormattedExtenso;

  // TODO(orquestrador): o mesmo padrão de useAgendaAberta (SWR + chave por
  // médico/procedimento). Hoje resposta atrasada pode pintar o calendário
  // com datas de outra busca.
  useEffect(() => {
    const fetchDatesWithAppointments = async () => {
      if (!codConvenio || !Number.isFinite(codMedico) || activeStep !== 2) {
        return;
      }

      try {
        // /next-appointments/123?procedimentos=CONSULTA,ECO
        let procedimentos = 'CONSULTA';
        if (codMedico === 4860 || codMedico === 1981) {
          procedimentos = procedimento;
        }
        if (codMedico === 2921) {
          procedimentos = procedimento || 'HOLTER,MAPA';
        }

        const response = await api.get(
          `/next-appointments/${codMedico}?procedimentos=${encodeURIComponent(
            procedimentos
          )}`
        );
        const dados = Array.isArray(response.data) ? response.data : [];
        setDatesAvailable(dados.map(a => parseISO(a.DATAAGENDA)));
      } catch (error) {
        toast.error(
          error?.response?.data?.error || 'Erro ao buscar datas disponíveis!'
        );
        setDatesAvailable([]);
      }
    };

    fetchDatesWithAppointments();
  }, [codConvenio, codMedico, activeStep, procedimento]);

  const limparSelecaoAgendamento = useCallback(() => {
    setActiveStep(0);
    setHorario();
    setAgenda([]);
    setSelectedIdxAgenda();
    setSelectedIdxMed();
    setSelectedIdxConv();
    setCodMedico();
    setNomeMedico();
    setCodConvenio();
    setSiglaConvenio();
    setMedicos(medicoAgendaPac);
    setWhatsappEnviado(false);
    setWhatsappFalha(null);
    setIsSubmitting(false);
  }, []);

  const handleReset = useCallback(() => {
    limparSelecaoAgendamento();
    setAlert({ show: false, variant: 'primary', mensagem: '' });
  }, [limparSelecaoAgendamento]);

  const loadPaciente = useCallback(async () => {
    try {
      const rawCpf = cpf ? cpf.trim().replace(/\D/g, '') : '';
      if (!validarCPF(rawCpf)) {
        setIsMensagem(false);
        setPacientePendente(false);
        setCpfInvalido(true);
        return toast.error('CPF do Paciente é inválido.');
      } else {
        setCpfInvalido(false);
      }

      const response = await api.get(`/pacientes/cpf`, {
        params: { cpf: rawCpf },
      });

      if (response.data.length === 0) {
        setPacientePendente(false);
        setIsMensagem(true);
        setIsAgendamento(false);
        return;
      }

      const pac = response.data[0];
      setPaciente(pac);
      setNewPhone(pac?.CELULARPAC);

      if (isPacientePendente(pac)) {
        setPacientePendente(true);
        setIsMensagem(false);
        setIsAgendamento(false);
        return;
      }

      setPacientePendente(false);
      setIsMensagem(false);
      setIsAgendamento(true);
    } catch (error) {
      setPacientePendente(false);
      setIsAgendamento(false);
      setIsMensagem(true);

      const status = error?.response?.status;
      const mensagem = error?.response?.data?.error || 'Erro inesperado.';

      if (status === 422) {
        setAlert({
          show: true,
          variant: 'warning',
          mensagem: `${mensagem}!`,
        });
      } else {
        setAlert({
          show: true,
          variant: 'danger',
          mensagem: `${mensagem}!`,
        });
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      limparSelecaoAgendamento();
    }
  }, [cpf, limparSelecaoAgendamento]);

  useEffect(() => {
    loadPaciente();
  }, [loadPaciente]);

  useEffect(() => {
    if (
      ['BIOIMPEDANCIA', 'ECO-FETAL', 'HOLTER', 'MAPA'].includes(procedimento)
    ) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [procedimento]);

  const temHorarioEProtocolo = () =>
    Boolean((horario ? horario : agenda?.HORARIO) && agenda?.protocolo);

  const handleNext = () => {
    if (activeStep === 3 && !temHorarioEProtocolo()) {
      toast.warn('Selecione um horário antes de continuar!');
      return;
    }

    const pularPassoMedico = activeStep === 0 && devePularPassoMedico;
    setActiveStep(
      prevActiveStep => prevActiveStep + (pularPassoMedico ? 2 : 1)
    );
  };

  const handleBack = () => {
    setAlert({ show: false, variant: '', mensagem: '' });
    if (activeStep === 1) setMedicos(medicoAgendaPac);

    if (activeStep === 2 || activeStep === 3) {
      setHorario();
      setAgenda([]);
      setSelectedIdxAgenda();
    }

    const voltarAoConvenio = activeStep === 2 && devePularPassoMedico;
    setActiveStep(prevActiveStep =>
      voltarAoConvenio ? 0 : prevActiveStep - 1
    );
  };

  const handleChangeDtAgenda = dtAgenda => {
    const selectedDate = new Date(dtAgenda);
    selectedDate.setHours(12, 0, 0, 0);
    setDateAgenda(selectedDate);
    setHorario();
    setAgenda([]);
    setSelectedIdxAgenda();
  };

  const handleHorarioClick = (index, horaAgenda, objAgenda) => {
    if (!horaAgenda || !objAgenda) {
      toast.warn('Horário inválido. Selecione outro horário disponível!');
      return;
    }

    // Sem DATAAGENDA no payload, a troca de dia + SWR já esvazia a lista.
    if (objAgenda.DATAAGENDA && !slotPertenceAoDia(objAgenda, dateAgenda)) {
      toast.warn('Esse horário não é do dia selecionado. Escolha de novo.');
      return;
    }

    const agendaSelecionada = { ...objAgenda };
    agendaSelecionada.codPac = paciente?.CODPAC;
    agendaSelecionada.situacao = 'Agendada';
    agendaSelecionada.codConv = codConvenio;
    agendaSelecionada.atividade = agendaSelecionada.ATIVIDADE;

    let descProcedi = procedimento || agendaSelecionada.ATIVIDADE;

    // TODO(orquestrador): mapa ATIVIDADE → CODPROCEDI (ver procedimentos.enum.js).
    switch (descProcedi) {
      case 'BIOIMPEDANCIA':
        agendaSelecionada.codProcedi = 801;
        break;
      case 'ECO-FETAL':
        agendaSelecionada.codProcedi = 17;
        break;
      case 'ECO':
        agendaSelecionada.codProcedi = 9;
        break;
      case 'HOLTER':
        agendaSelecionada.codProcedi = 6;
        break;
      case 'MAPA':
        agendaSelecionada.codProcedi = 7;
        break;
      default:
        agendaSelecionada.codProcedi = 1;
    }

    agendaSelecionada.protocolo = `ICM-${new Date().getTime()}`;
    setHorario(horaAgenda);
    setAgenda(agendaSelecionada);
    setSelectedIdxAgenda(index);
    handleNext();
  };

  const handleConfirm = async () => {
    if (isSubmitting || isPacientePendente(paciente)) return;

    if (!agenda?.CODAGENDA) {
      toast.warn('Selecione um horário antes de continuar!');
      return;
    }

    if (agenda.DATAAGENDA && !slotPertenceAoDia(agenda, dateAgenda)) {
      toast.warn('Esse horário não é do dia selecionado. Escolha de novo.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.put('agendamento-online', { agenda });
      toast.success('AGENDAMENTO REALIZADO COM SUCESSO!');

      const envioWhatsApp = await enviarMensagemWhatsApp();
      setWhatsappEnviado(envioWhatsApp.ok);
      setWhatsappFalha(envioWhatsApp.ok ? null : envioWhatsApp.code);
      setActiveStep(5);
    } catch (err) {
      const details = err.response?.data?.details
        ?.map(detail => detail.message)
        .filter(Boolean)
        .join(' ');
      setAlert({
        show: true,
        variant: 'danger',
        mensagem: details || err.response?.data?.error || 'Erro inesperado.',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      limparSelecaoAgendamento();
    } finally {
      setIsSubmitting(false);
    }
  };

  // TODO(orquestrador): mensagemWhatsAppAgendamento({ paciente, agenda, ... }) puro.
  const getMensagemAgendamento = protocoloFallback =>
    `*📋 CONFIRMAÇÃO DE AGENDAMENTO - ICM*

👤 *Paciente:* ${paciente?.NOMEPAC}
🔢 *Protocolo:* ${agenda?.protocolo || protocoloFallback}

👨‍⚕️ *Médico:* ${nomeMedico}
💳 *Convênio:* ${siglaConvenio}
📅 *Data:* ${dateFormattedExtensoSlot} - (${dateFormattedSlot})
🕐 *Horário:* ${formatarHora(horario ? horario : agenda?.HORARIO)}

📍 *Local:* Av. Vicente Ferreira 780 – ao lado do P.S. da Santa Casa
🚗 *Estacionamento:* Rua Amazonas, 777

⚠️ *Chegar com 15 minutos de antecedência.*

📞 Em caso de dúvidas: (14) 3402-5252
🌐 www.icm.com.br`;

  const enviarMensagemWhatsApp = async () => {
    const celular = normalizeWhatsappPhone(paciente?.CELULARPAC);

    if (!celular) {
      toast.warn('Celular não encontrado ou inválido para envio via WhatsApp!');
      return { ok: false, code: null };
    }

    if (!temHorarioEProtocolo()) {
      toast.warn(
        'Não foi possível enviar a confirmação: horário ou protocolo ausente!'
      );
      return { ok: false, code: null };
    }

    try {
      const response = await api.post('/instance/message', {
        to: celular,
        text: getMensagemAgendamento(''),
      });

      if (response.data) {
        toast.success('Confirmação enviada para seu WhatsApp!');
        return { ok: true, code: null };
      }
      return { ok: false, code: 'WHATSAPP_SEND_FAILED' };
    } catch (error) {
      toast.warn(
        error.response?.data?.error ||
        'Erro ao enviar confirmação via WhatsApp!'
      );
      return {
        ok: false,
        code: error.response?.data?.code || 'WHATSAPP_SEND_FAILED',
      };
    }
  };

  const handleEnviarWhatsApp = () => {
    if (!temHorarioEProtocolo()) {
      toast.warn(
        'Não foi possível enviar a confirmação: horário ou protocolo ausente!'
      );
      return;
    }

    const celularComDdi = normalizeWhatsappPhone(paciente?.CELULARPAC);
    if (!celularComDdi) {
      toast.warn('Celular não encontrado ou inválido para envio via WhatsApp!');
      return;
    }

    const celular = celularComDdi.replace(/^\+/, '');
    const mensagemEncoded = encodeURIComponent(getMensagemAgendamento('N/A'));

    // Abre o WhatsApp com a mensagem preenchida para o próprio paciente salvar/encaminhar
    const whatsappUrl = celular
      ? `https://api.whatsapp.com/send?phone=${celular}&text=${mensagemEncoded}`
      : `https://api.whatsapp.com/send?text=${mensagemEncoded}`;

    window.open(whatsappUrl, '_blank');
  };

  // TODO(orquestrador): helper puro `medicosPorConvenio(codConv)` + handleNext
  // no clique. Hoje mistura filtro, setState e avanço de passo.
  function checkConvenio(codConv) {
    const listMedicos = [];

    // 25 Particular Todos  - (1/5/958/957/24/2030/2657/2796/3283/1628/3794/4190)
    // 1 Cabesp Todos
    if (codConv === 25 || codConv === 1) {
      medicoAgendaPac.forEach(medico => {
        listMedicos.push(medico);
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 14 / 68 /69 /70 Unimed menos 5 Fábio, 1 Braga e 1628 Roberto
    // 2796 dr joao
    // if (codConv === 14 || codConv === 68 || codConv === 69 || codConv === 70) {
    if (codConv === 14 || codConv === 68 || codConv === 70) {
      medicoAgendaPac.forEach(medico => {
        if (
          medico.codigo !== 1 &&
          medico.codigo !== 5 &&
          medico.codigo !== 1628
        ) {
          listMedicos.push(medico);
        }
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 65 Unimed Jacto Todos (1/5/958/957/24/2030/2657/2796/3283/3794/4190) menos 1628
    if (codConv === 65) {
      medicoAgendaPac.forEach(medico => {
        if (medico.codigo !== 1628) listMedicos.push(medico);
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 69 Unimed Intercambio - Dr Joao, DR Fabio e Dr Roberto NAO atendem
    if (codConv === 69) {
      medicoAgendaPac.forEach(medico => {
        if (
          medico.codigo !== 2796 &&
          medico.codigo !== 1628 &&
          medico.codigo !== 5
        ) {
          listMedicos.push(medico);
        }
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 732 São Francisco Médicos que atendem: 4190 Érica, 3283 Igor,
    // 2657 Marina, 3794 Raphael e 1628 Roberto
    if (codConv === 732) {
      medicoAgendaPac.forEach(med => {
        if (
          med.codigo === 4190 ||
          med.codigo === 2657 ||
          med.codigo === 3794 ||
          med.codigo === 1628
        ) {
          listMedicos.push(med);
        }
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 43 Bradesco Saúde Menos 1 Dr.Braga e Dr Joao 2796
    // 2 Cassi Menos Dr.Braga e Dr Joao 2796
    // 4 Economus Menos Dr.Braga1 e Dr Joao 2796
    // 108 - AMIL Menos Dr.Braga1 e Dr Joao 2796
    if (codConv === 43 || codConv === 801 || codConv === 2) {
      medicoAgendaPac.forEach(med => {
        if (med.codigo !== 1 && med.codigo !== 2796) {
          listMedicos.push(med);
        }
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    // 5 Saúde Caixa Menos Dr.Braga 1
    // 3 Funcesp Menos Dr.Braga1
    // 20 APAS Menos Dr.Braga 1
    // 23 ACE Menos Dr.Braga 1
    // 7 Afresp Menos Dr.Braga 1
    // 13 SSPP Menos Dr.Braga 1
    if (codConv === 7 || codConv === 13 || codConv === 20 || codConv === 23) {
      medicoAgendaPac.forEach(med => {
        if (med.codigo !== 1) listMedicos.push(med);
      });
      setMedicos(listMedicos);
      setCodConvenio(codConv);
    }
    return handleNext();
  }

  // TODO: isso tambem precisa ser validado no backend.
  const isCardioPediatraBloqueado = (codMed, nomeMed) => {
    if (!cardioPediatras.includes(Number(codMed))) return false;

    const dtNascimento = Date.parse(paciente?.dataFormatada);
    const idade = differenceInYears(new Date(), new Date(dtNascimento));
    if (!(idade > 14)) return false;

    toast.warn(
      `Médico ${nomeMed} é Cardio Pediatra e só atende pacientes até 14 anos. Favor agendar com outro médico da equipe que atenda o seu convênio!`
    );
    handleReset();
    return true;
  };

  const handleMedicoClick = (index, codMed, nomeMed) => {
    if (isCardioPediatraBloqueado(codMed, nomeMed)) return;

    setSelectedIdxMed(index);
    setCodMedico(codMed);
    setNomeMedico(nomeMed);
    handleNext();
  };

  const handleConvenioClick = (index, codConv, siglaConv) => {
    const medico = Number.isFinite(codMedicoParam)
      ? medicoAgendaPac.find(m => m.codigo === codMedicoParam)
      : undefined;

    setSelectedIdxConv(index);
    setCodConvenio(codConv);
    setSiglaConvenio(siglaConv);
    checkConvenio(codConv);

    if (Number.isFinite(codMedicoParam)) {
      setCodMedico(codMedicoParam);
      setNomeMedico(medico ? medico.medico : '');
    }
  };

  const formatPhoneNumber = value => {
    setMensagem('');
    setErro('');
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{5})(\d{4})$/);
    if (match) return `(${match[1]})${match[2]}-${match[3]}`;
    return value;
  };

  const handleAtualizaCelular = async () => {
    try {
      setIsNewPhone(!isNewPhone);
      const celularValido = /^\(0\d{2}\)9\d{4}-\d{4}$/;
      if (!celularValido.test(newPhone)) {
        setErro('Formato de celular inválido! Ex: (111)11111-1111');
        return;
      }

      const response = await api.put(`/paciente/celular`, {
        codPac: paciente?.CODPAC,
        celular: newPhone,
      });
      setMensagem(response?.data?.message);
      setErro('');
    } catch (error) {
      setErro(error.response?.data?.error || 'Erro ao atualizar o celular');
      setMensagem('');
    }
  };

  return (
    <>
      {cpfInvalido && (
        <div className="text-center w-50 mx-auto mt-4">
          <IcmHeaderImage style={{ marginBottom: 16 }} />
          <Alert variant="danger" className="text-center">
            <Alert.Heading>Atenção!</Alert.Heading>
            <p>
              O CPF informado não é válido. Por favor, confira o número digitado
              e tente novamente.
            </p>
            <p>
              <span className="text-muted">
                Se precisar de ajuda, entre em contato com a equipe ICM.
              </span>
            </p>
          </Alert>
        </div>
      )}

      {pacientePendente && (
        <header className="text-center m-4">
          <IcmHeaderImage style={{ marginBottom: 16 }} />
          <Alert
            variant="info"
            className="text-center mx-auto"
            style={{ maxWidth: 800 }}
          >
            <Alert.Heading>
              Olá, <strong>{paciente?.NOMEPAC}</strong>!
            </Alert.Heading>
            <p className="mt-3">
              No momento, não foi possível prosseguir com o seu agendamento pelo
              portal online.
            </p>
            <p>
              Para realizarmos o seu agendamento com toda a atenção necessária,
              pedimos a gentileza de entrar em contato diretamente com a nossa
              equipe de atendimento pelo telefone:{' '}
              <strong>
                <a href="tel:1434025252">(14) 3402-5252</a>
              </strong>
              .
            </p>
            <p className="mb-0">
              Nossa equipe terá o maior prazer em auxiliá-lo. Agradecemos pela
              compreensão!
            </p>
          </Alert>
        </header>
      )}

      {/* TODO(orquestrador): CadastroPacienteOnline — submit fora do JSX. */}
      {isMensagem && (
        <header className="text-center m-4">
          <IcmHeaderImage />
          <div
            className="alert alert-warning mt-4 text-left mx-auto style-container"
            style={{ maxWidth: 800 }}
          >
            <h4 className="alert-heading text-center">
              Paciente não encontrado!
            </h4>
            <p className="text-center">
              Não encontramos nenhum cadastro associado ao CPF informado (
              <strong>{cpf}</strong>).
            </p>
            <p className="text-center">
              Por favor, confira os dados digitados. Se precisar de ajuda,
              estamos à disposição para auxiliá-lo.
            </p>
            <hr />
            <h5 className="mb-3">Cadastre-se para agendar seu procedimento:</h5>
            <Form
              className="mb-3"
              onSubmit={async e => {
                e.preventDefault();
                const nome = e.target.nome.value.trim();
                const dtNasc = e.target.dtNasc.value;
                const cpfVal = e.target.cpf.value.trim();
                const sexo = e.target.sexo.value;
                const celular = e.target.celular.value.trim();
                const informacao = e.target.informacao.value.trim();

                const nomeSanitizado = nome
                  .normalize('NFD')
                  .replace(/[^\w\s]/g, '')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toUpperCase();

                if (!nomeSanitizado || nomeSanitizado.length < 3) {
                  toast.error('Nome inválido ou muito curto!');
                  return;
                }
                if (!dtNasc) {
                  toast.error('Data de nascimento obrigatória!');
                  return;
                }
                if (!cpfVal || cpfVal.length !== 11) {
                  toast.error('CPF inválido!');
                  return;
                }
                if (!sexo || !['M', 'F'].includes(sexo)) {
                  toast.error('Selecione o sexo corretamente!');
                  return;
                }
                const celularNumeros = celular.replace(/\D/g, '');
                if (!celularNumeros || celularNumeros.length < 10) {
                  toast.error('Celular inválido! Digite ao menos 10 números.');
                  return;
                }

                try {
                  await api.post('/new-paciente-online', {
                    nome: nomeSanitizado,
                    dtNasc,
                    cpf: cpfVal,
                    sexo,
                    celular,
                    informacao: `CADASTRO ONLINE: ` + informacao,
                  });
                  toast.success('Cadastro realizado! Agende sua consulta.');
                  // Recarrega o cadastro do paciente para habilitar a tela de agendamento diretamente
                  loadPaciente();
                } catch (err) {
                  toast.error(err?.response?.data?.error || 'Erro no cadastro');
                }
              }}
            >
              <Form.Group>
                <Form.Label>Nome Completo</Form.Label>
                <Form.Control
                  name="nome"
                  required
                  style={{ textTransform: 'uppercase' }}
                  maxLength={60}
                  pattern="^[A-Za-z ]{3,}$"
                  title="Digite nome completo, apenas letras e espaços simples."
                />
              </Form.Group>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Data de Nascimento</Form.Label>
                    <Form.Control
                      name="dtNasc"
                      type="date"
                      required
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>CPF</Form.Label>
                    <Form.Control
                      name="cpf"
                      required
                      style={{ textTransform: 'uppercase' }}
                      value={cpf || ''}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Sexo</Form.Label>
                    <Form.Control
                      as="select"
                      name="sexo"
                      required
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Selecione
                      </option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Celular</Form.Label>
                    <Form.Control
                      name="celular"
                      required
                      placeholder="Somente números"
                      style={{ textTransform: 'uppercase' }}
                      pattern="^\d{10,}$"
                      title="Digite apenas números, mínimo 10 dígitos"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Informação Adicional</Form.Label>
                <Form.Control
                  as="textarea"
                  name="informacao"
                  rows={2}
                  placeholder="Ex: Endereço, convênio, número da carteirinha, alergias, observações, etc."
                />
              </Form.Group>
              <div className="text-center">
                <Button type="submit" variant="primary" size="lg">
                  Cadastrar
                </Button>
              </div>
            </Form>
            <hr />
            <p className="text-center">
              Para entrar em contato com nossa equipe, clique no botão abaixo
              para conversar via WhatsApp:
            </p>
            <div className="text-center">
              <a
                href="https://api.whatsapp.com/send?phone=5514998974587&text=Olá,%20não%20encontrei%20meu%20cadastro%20no%20sistema.%20Poderiam%20me%20ajudar?"
                className="btn btn-success btn-lg"
                target="_blank"
                rel="noopener noreferrer"
              >
                Falar com a Clínica no WhatsApp
              </a>
            </div>
            <p className="mt-3 text-center">
              Ou, se preferir, ligue para nós pelo telefone:{' '}
              <strong>(14) 3402-5252</strong>.
            </p>
            <p className="mb-0 text-center">
              Estamos aqui para garantir o melhor atendimento. Agradecemos pela
              sua compreensão!
            </p>
            <AppFooter />
          </div>
        </header>
      )}

      {isAgendamento && (
        <div className="container py-3">
          <div className="text-center">
            <IcmHeaderImage />
          </div>
          <Alert variant="info" className="text-center w-75 mx-auto">
            <Alert.Heading>
              Olá, <strong>{paciente?.NOMEPAC}</strong>, agende sua consulta de
              forma rápida e prática!
            </Alert.Heading>
            Seu número de celular é <strong>{paciente?.CELULARPAC}</strong>.
            Caso esteja desatualizado, você pode alterá-lo.
            <Button onClick={() => setIsNewPhone(!isNewPhone)} className="ml-2">
              Atualizar
            </Button>
          </Alert>

          {/* TODO(orquestrador): AlertaPreparoProcedimento — textos por procedimento. */}
          {showAlert && (
            <Alert
              variant="success"
              onClose={() => setShowAlert(false)}
              dismissible
            >
              <Alert.Heading>Atenção!</Alert.Heading>
              <p>
                Olá, {paciente?.NOMEPAC}, você selecionou o procedimento{' '}
                <strong>{procedimento}</strong>
              </p>

              {procedimento === 'ECO-FETAL' && (
                <div>
                  <hr />
                  <p>
                    📌 <strong>ECODOPPLERCARDIOGRAMA FETAL</strong>
                    <br />
                    🔹 Orientações / Preparo:
                    <br />
                    - Dura cerca de 20 minutos, podendo se estender dependendo
                    da posição e movimentação do feto.
                    <br />
                    - Período recomendado: 24 a 28 semanas de gestação.
                    <br />
                    - Se gestação gemelar, agendar um horário para cada feto.
                    <br />
                    ⚠️ <strong>CONVÊNIO UNIMED:</strong> Para atendimentos pela
                    Unimed, o procedimento precisa estar autorizado no código{' '}
                    <strong>40.90.10.84</strong>
                  </p>
                </div>
              )}

              {procedimento === 'BIOIMPEDANCIA' && (
                <div>
                  <hr />
                  📌 <strong>BIOIMPEDÂNCIA</strong>
                  <br />
                  🔹 Orientações / Preparo:
                  <br />
                  - Evitar café ou atividade física nas 12h que antecedem o
                  exame.
                  <br />
                  - Jejum de alimentos e líquidos nas 2h anteriores.
                  <br />
                  - Não ingerir bebidas alcoólicas nas 24h anteriores.
                  <br />
                  - Não passar creme nos pés e mãos.
                  <br />
                  - Gestantes, portadores de marcapasso ou pinos ortopédicos não
                  podem realizar o exame.
                  <br />
                  ⚠️ <strong>CONVÊNIO UNIMED:</strong> Para atendimentos pela
                  Unimed, o procedimento precisa estar autorizado no código{' '}
                  <strong>41.30.10.48</strong> para Marília-SP
                </div>
              )}

              {procedimento === 'HOLTER' && (
                <div>
                  <hr />
                  📌 <strong>HOLTER</strong>
                  <br />
                  🔹 Orientações / Preparo:
                  <br />
                  - Vir de cinto.
                  <br />
                  - Vir de banho tomado.
                  <br />
                  - Não vir de vestido.
                  <br />
                  - Não usar cremes, óleos ou sabonetes hidratantes na região do
                  tórax.
                  <br />
                  ⚠️ <strong>CONVÊNIO UNIMED:</strong> Para atendimentos pela
                  Unimed, o procedimento precisa estar autorizado no código{' '}
                  <strong>20.10.20.20</strong> para realização em Marília-SP.
                </div>
              )}

              {procedimento === 'MAPA' && (
                <div>
                  <hr />
                  📌 <strong>MAPA</strong>
                  <br />
                  🔹 Orientações / Preparo:
                  <br />
                  - Vir com blusa de manga larga.
                  <br />
                  - Vir de cinto.
                  <br />
                  - Vir de banho tomado.
                  <br />
                  - Não usar cremes, óleos ou sabonetes hidratantes.
                  <br />
                  ⚠️ <strong>CONVÊNIO UNIMED:</strong> Para atendimentos pela
                  Unimed, o procedimento precisa estar autorizado no código{' '}
                  <strong>20.10.20.38</strong> para realização em Marília-SP.
                </div>
              )}
            </Alert>
          )}

          {/* TODO(orquestrador): FormAtualizarCelular. */}
          {isNewPhone && (
            <Card className="m-2 w-25 mx-auto">
              <Card.Header className="font-weight-bold">
                Atualizar Número de Celular
              </Card.Header>
              <Card.Body>
                <Form.Label>Número atual: {paciente?.CELULARPAC}</Form.Label>
                <InputMask
                  type="text"
                  style={{ backgroundColor: 'rgba(255, 255, 0, 0.3)' }}
                  mask="(111)11111-1111"
                  onChange={e => setNewPhone(formatPhoneNumber(e.target.value))}
                  name="CELULARPAC"
                  value={newPhone}
                />
              </Card.Body>
              <Card.Footer className="text-center">
                <Button
                  variant="secondary"
                  onClick={() => setIsNewPhone(!isNewPhone)}
                >
                  Cancelar
                </Button>{' '}
                <Button onClick={handleAtualizaCelular}>Confirmar</Button>
              </Card.Footer>
            </Card>
          )}

          {mensagem && (
            <Alert className="text-center mt-3" variant="success">
              {mensagem}
            </Alert>
          )}

          {erro && (
            <Alert className="text-center mt-3" variant="danger">
              {erro}
            </Alert>
          )}

          {alert.show && (
            <Alert variant={alert.variant} className="text-center">
              {alert.mensagem}
            </Alert>
          )}

          {/* TODO(orquestrador): renderizar só o passo ativo; Voltar/Alterar no layout do passo. */}
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {getStepContent(
                    index,
                    selectedIdxMed,
                    selectedIdxConv,
                    selectedIdxAgenda,
                    handleMedicoClick,
                    handleConvenioClick,
                    medicos,
                    paciente,
                    convenioAgendaPac,
                    dateFormattedSlot,
                    dateFormattedExtensoSlot,
                    horariosAgenda,
                    carregandoHorarios,
                    handleHorarioClick,
                    formatarHora,
                    handleChangeDtAgenda,
                    dateAgenda,
                    datesAvailable,
                    nomeMedico,
                    siglaConvenio,
                    horario,
                    handleConfirm,
                    handleReset,
                    agenda?.protocolo,
                    handleEnviarWhatsApp,
                    handleNext,
                    isSubmitting,
                    whatsappEnviado,
                    whatsappFalha
                  )}
                  {index < 4 && (
                    <div className="mt-3">
                      <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        variant="secondary"
                        size="sm"
                        className="mr-2"
                      >
                        Voltar
                      </Button>
                    </div>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </div>
      )}
    </>
  );
}
